import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MenuBarMenu from './menu-bar-menu.jsx';
import { MenuItem, MenuSection } from '../menu/menu.jsx';
import dropdownCaret from './dropdown-caret.svg';
import styles from './profile-button.css';

const ProfileButton = ({ isRtl, onOpenFeedbackModal, userTier, isScratchDesktop, onCheckUpdate, onCheckAgentUpdate }) => {
    const [user, setUser] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);

    useEffect(() => {
        // Get user data from localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                setUser(JSON.parse(userData));
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
    }, []);

    const handleClick = () => {
        setIsOpen(!isOpen);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleUpdateCheck = () => {
        handleClose();
        if (onCheckAgentUpdate) {
            onCheckAgentUpdate();
        } else if (onCheckUpdate) {
            onCheckUpdate();
        } else {
            alert('You are using TIF Studio Web. The web application is always up to date with the latest features!');
        }
    };

    const handleLogout = () => {
        // Check if user had Remember Me enabled
        const userData = localStorage.getItem('user');
        let shouldKeepCredentials = false;

        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                shouldKeepCredentials = parsedUser.rememberMe === true;
            } catch (e) {
                // Ignore error
            }
        }

        // Clear user session data
        localStorage.removeItem('user');

        // Only clear remembered credentials if Remember Me was NOT enabled
        if (!shouldKeepCredentials) {
            localStorage.removeItem('rememberedCredentials');
        }

        // Dispatch custom event to notify App component
        window.dispatchEvent(new Event('userLogout'));

        // Force reload to ensure clean state
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    // Don't render if no user is logged in
    if (!user) {
        return null;
    }

    return (
        <div className={styles.profileButtonContainer} ref={buttonRef}>
            <div
                className={classNames(styles.profileButton, {
                    [styles.active]: isOpen
                })}
                onClick={handleClick}
                title="User Options"
            >
                <div className={styles.avatar}>
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <img
                    className={styles.dropdownCaret}
                    src={dropdownCaret}
                    alt="dropdown"
                />
            </div>
            <MenuBarMenu
                className={styles.menuBarMenu}
                open={isOpen}
                place={isRtl ? 'right' : 'left'}
                onRequestClose={handleClose}
            >
                <div className={styles.userInfo}>
                    <div className={styles.userNameHeader}>Hi , {user.name}</div>
                    <div className={classNames(styles.versionTag, {
                        [styles.proVersion]: userTier === 'pro'
                    })}>
                        {userTier === 'pro' ? 'Pro Version' : 'Free Version'}
                    </div>
                </div>
                {userTier !== 'pro' && (
                    <MenuSection>
                        <MenuItem
                            onClick={() => window.open('https://tifstudio.in/index.html#pricing', '_blank')}
                            className={styles.upgradeMenuItem}
                        >
                            <span className={styles.upgradeButton}>Upgrade to Pro</span>
                        </MenuItem>
                    </MenuSection>
                )}
                <MenuSection>
                    <MenuItem onClick={onOpenFeedbackModal}>
                        Feedback
                    </MenuItem>
                </MenuSection>
                <MenuSection>
                    <MenuItem onClick={handleUpdateCheck}>
                        Check for Updates
                    </MenuItem>
                </MenuSection>
                <MenuSection>
                    <MenuItem onClick={handleLogout} className={styles.logoutItem}>
                        <span className={styles.logoutText}>Logout</span>
                    </MenuItem>
                </MenuSection>
            </MenuBarMenu>
        </div>
    );
};

ProfileButton.propTypes = {
    isRtl: PropTypes.bool,
    onOpenFeedbackModal: PropTypes.func,
    userTier: PropTypes.string,
    isScratchDesktop: PropTypes.bool,
    onCheckUpdate: PropTypes.func,
    onCheckAgentUpdate: PropTypes.func
};

export default ProfileButton;
